import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { Check, ChevronsUpDown } from 'lucide-react';
import { validateEmail, validatePhone } from '../utils/validators';
import { generateSecurePassword } from '../utils/password';
import { OtroLugarDialog, SubmittedView } from './cauform/index';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.22, ease: [0.2, 0.8, 0.2, 1] } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };

const initialForm = { nombre: '', correo_personal: '', correo_corporativo: '', contrasena: '', lugar_trabajo: '', fecha_incorporacion: '', telefono: '', correo_responsable: '', tienePortatil: true, tipoMovil: '', tokenExpiration: 48 };

export default function CAUForm() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [lugaresTrabajo, setLugaresTrabajo] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [noPhone, setNoPhone] = useState(false);
  const [openLugar, setOpenLugar] = useState(false);
  const [showOtroDialog, setShowOtroDialog] = useState(false);
  const [otroLugar, setOtroLugar] = useState({ codigo: '', descripcion: '' });
  const [savingOtro, setSavingOtro] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [draftPassword, setDraftPassword] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(false);

  // Load draft if id in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      setLoadingDraft(true);
      fetch(`${BACKEND_URL}/api/onboardings/${id}`)
        .then(r => r.json())
        .then(data => {
          if (data.status === 'draft') {
            setDraftId(id);
            setDraftPassword(data.decrypted_password);
            setForm({
              nombre: data.nombre || '',
              correo_personal: data.correo_personal || '',
              correo_corporativo: data.correo_corporativo || '',
              contrasena: data.decrypted_password || '',
              lugar_trabajo: data.lugar_trabajo || '',
              fecha_incorporacion: data.fecha_incorporacion || '',
              telefono: data.telefono || '',
              tienePortatil: data.tienePortatil !== undefined ? data.tienePortatil : true,
              tipoMovil: data.tipoMovil || '',
              tokenExpiration: data.token_expiration_hours || 48,
              correo_responsable: data.correo_responsable || '',
            });
            if (data.telefono === 'No disponible') setNoPhone(true);
            toast.success('Borrador cargado');
          } else {
            toast.error('Este alta ya ha sido enviada');
          }
        })
        .catch(() => toast.error('Error cargando borrador'))
        .finally(() => setLoadingDraft(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - BACKEND_URL is environment constant

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/lugares-trabajo`).then(r => r.json()).then(d => setLugaresTrabajo(d.lugares || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - BACKEND_URL is environment constant

  const handleSaveOtroLugar = async () => {
    if (!otroLugar.codigo.trim() || !otroLugar.descripcion.trim()) {
      toast.error('Código y descripción son obligatorios');
      return;
    }

    setSavingOtro(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/lugares-trabajo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(otroLugar)
      });

      const data = await res.json();

      if (data.success) {
        toast.success('Lugar de trabajo añadido correctamente');
        // Update list and select the new one
        const nuevoLugar = data.lugar;
        setLugaresTrabajo(prev => [...prev, nuevoLugar].sort());
        handleChange('lugar_trabajo', nuevoLugar);
        setShowOtroDialog(false);
        setOpenLugar(false);
        setOtroLugar({ codigo: '', descripcion: '' });
      } else {
        toast.error('Error al guardar');
      }
    } catch (error) {
      toast.error('Error al guardar el lugar de trabajo');
    } finally {
      setSavingOtro(false);
    }
  };

  const handleNoPhone = (checked) => {
    setNoPhone(checked);
    if (checked) { setForm(p => ({ ...p, telefono: 'No disponible' })); setErrors(p => ({ ...p, telefono: undefined })); }
    else { setForm(p => ({ ...p, telefono: '' })); }
  };

  const validate = () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = 'Campo obligatorio.';
    if (!form.correo_personal.trim()) e.correo_personal = 'Campo obligatorio.';
    else if (!validateEmail(form.correo_personal)) e.correo_personal = 'Correo no válido.';
    if (!form.correo_corporativo.trim()) e.correo_corporativo = 'Campo obligatorio.';
    else if (!validateEmail(form.correo_corporativo)) e.correo_corporativo = 'Correo no válido.';
    if (form.correo_responsable.trim() && !validateEmail(form.correo_responsable)) e.correo_responsable = 'Correo no válido.';
    if (!form.contrasena.trim()) e.contrasena = 'Campo obligatorio.';
    else if (form.contrasena.length < 4) e.contrasena = 'Mín. 4 caracteres.';
    if (!form.lugar_trabajo) e.lugar_trabajo = 'Seleccione lugar de trabajo.';
    if (!form.fecha_incorporacion) e.fecha_incorporacion = 'Seleccione fecha.';
    // Teléfono es opcional, solo validar formato si tiene contenido
    if (form.telefono.trim() && !noPhone) {
      if (!validatePhone(form.telefono)) e.telefono = 'Teléfono no válido.';
    }
    return e;
  };

  const handleChange = (name, value) => {
    setForm(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: undefined }));
  };

  const handleSubmit = async (e, saveAsDraft = false) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); toast.error('Revise los campos marcados.'); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        save_as_draft: saveAsDraft,
        token_expiration_hours: form.tokenExpiration,
        correo_responsable: form.correo_responsable.trim() || null,
      };
      if (noPhone) payload.telefono = 'No disponible';

      // === CASO 1: Editando un draft existente Y pulsando "Enviar ahora" ===
      // Se guarda primero como draft (PUT) y luego se envía (PUT /send)
      // para que el MISMO documento transicione a status='sent' sin duplicar.
      if (draftId && !saveAsDraft) {
        // 1a. Guardar cambios en el draft
        const updateRes = await fetch(`${BACKEND_URL}/api/onboardings/${draftId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, save_as_draft: true })
        });
        if (!updateRes.ok) {
          const d = await updateRes.json();
          throw new Error(d.detail || 'Error guardando cambios del borrador');
        }

        // 1b. Disparar envío de emails (transiciona a 'sent')
        const sendRes = await fetch(`${BACKEND_URL}/api/onboardings/${draftId}/send`, {
          method: 'PUT'
        });
        if (!sendRes.ok) {
          const d = await sendRes.json();
          throw new Error(d.detail || 'Error enviando el alta');
        }
        const data = await sendRes.json();
        setResult(data);
        setSubmitted(true);
        toast.success('Alta procesada. Correos enviados correctamente.');
        return;
      }

      // === CASO 2: Nueva alta (sin draftId) o actualización de draft ===
      const isUpdate = draftId && saveAsDraft;
      const url = isUpdate
        ? `${BACKEND_URL}/api/onboardings/${draftId}`
        : `${BACKEND_URL}/api/onboardings`;
      const method = isUpdate ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Error del servidor'); }
      const data = await res.json();

      if (saveAsDraft) {
        if (!draftId) setDraftId(data.id); // Solo set si es nuevo
        setDraftPassword(form.contrasena);
        toast.success(isUpdate ? 'Borrador actualizado correctamente' : 'Borrador guardado correctamente');
      } else {
        setResult(data);
        setSubmitted(true);
        toast.success('Alta procesada. Correos enviados correctamente.');
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error(`Error: ${err.message}`);
    } finally { setLoading(false); }
  };

  const handleReset = () => { 
    setForm(initialForm); 
    setErrors({}); 
    setSubmitted(false); 
    setResult(null); 
    setNoPhone(false); 
    setDraftId(null);
    setDraftPassword(null);
    // Clear URL params
    window.history.replaceState({}, '', window.location.pathname);
  };
  const Err = ({ name }) => errors[name] ? <p style={{ color: '#dc2626', fontSize: '11.5px', marginTop: '3px' }}>{errors[name]}</p> : null;

  if (submitted && result) {
    return (
      <SubmittedView
        form={form}
        noPhone={noPhone}
        result={result}
        onReset={handleReset}
      />
    );
  }

  return (
    <div className="page-container">
      {/* HERO HEADER - full width */}
      <motion.div initial="hidden" animate="show" variants={fadeUp}>
        <div style={{
          background: 'linear-gradient(150deg, #0d3d1f 0%, #1a5c2e 40%, #2d8a4e 80%, #3aaa62 100%)',
          borderRadius: '16px', padding: '28px 32px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px',
          boxShadow: '0 4px 24px rgba(26,92,46,0.2)',
          position: 'relative', overflow: 'hidden',
        }}>
          {/* Background pattern */}
          <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            backgroundImage: 'radial-gradient(circle at 80% 50%, rgba(110,201,98,0.12) 0%, transparent 60%)',
          }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '18px', position: 'relative' }}>
            <img src="/valoriza-logo.svg" alt="Valoriza" style={{ height: '40px', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.2))' }} />
            <div style={{ borderLeft: '1px solid rgba(255,255,255,0.25)', paddingLeft: '18px' }}>
              <h1 style={{ fontFamily: 'Space Grotesk', fontSize: '22px', fontWeight: 700, color: '#fff', margin: 0 }}>Alta de Empleado IT</h1>
              <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '13px', margin: '2px 0 0' }}>Complete los datos para generar acceso y enviar emails de bienvenida</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', position: 'relative' }}>
            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '10px', padding: '10px 16px', textAlign: 'center' }}>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Emails</div>
              <div style={{ color: '#7fe5a0', fontSize: '11px', fontWeight: 600 }}>📧 Bienvenida + 🔐 Activación</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '20px', padding: '6px 14px' }}>
              <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#3aaa62', animation: 'pulse-green 2s infinite' }} />
              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px', fontWeight: 500 }}>Sistema activo</span>
            </div>
          </div>
        </div>
      </motion.div>

      <form onSubmit={handleSubmit}>
        <motion.div initial="hidden" animate="show" variants={stagger} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* ROW 1: Personal + Work side by side */}
          <motion.div variants={fadeUp} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }} className="form-row-2">

            {/* Datos personales */}
            <Card style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <CardHeader style={{ padding: '16px 20px 10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '28px', height: '28px', background: 'rgba(26,92,46,0.08)', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>👤</div>
                  <div><CardTitle style={{ fontSize: '14px', color: '#1a5c2e' }}>Datos personales</CardTitle><CardDescription style={{ fontSize: '12px' }}>Información del nuevo empleado</CardDescription></div>
                </div>
              </CardHeader>
              <Separator />
              <CardContent style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <Label style={{ fontSize: '12.5px', fontWeight: 600 }}>Nombre y apellidos <span style={{ color: '#dc2626' }}>*</span></Label>
                  <Input data-testid="cau-form-nombre-input" placeholder="Ana García López" value={form.nombre} onChange={e => handleChange('nombre', e.target.value)}
                    style={{ marginTop: '5px', height: '40px', fontSize: '14px', borderColor: errors.nombre ? '#dc2626' : undefined }} />
                  <Err name="nombre" />
                </div>
                <div>
                  <Label style={{ fontSize: '12.5px', fontWeight: 600 }}>Correo personal <span style={{ color: '#dc2626' }}>*</span></Label>
                  <Input data-testid="cau-form-correo-personal-input" type="email" placeholder="empleado@gmail.com" value={form.correo_personal} onChange={e => handleChange('correo_personal', e.target.value)}
                    style={{ marginTop: '5px', height: '40px', fontSize: '14px', borderColor: errors.correo_personal ? '#dc2626' : undefined }} />
                  <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>Aquí llegarán los correos de bienvenida</p>
                  <Err name="correo_personal" />
                </div>
                <div>
                  <Label style={{ fontSize: '12.5px', fontWeight: 600 }}>Correo responsable <span style={{ color: '#9ca3af', fontWeight: 400 }}>(opcional)</span></Label>
                  <Input
                    data-testid="cau-form-correo-responsable-input"
                    type="email"
                    placeholder="jefe@valorizasm.com"
                    value={form.correo_responsable}
                    onChange={e => handleChange('correo_responsable', e.target.value)}
                    style={{ marginTop: '5px', height: '40px', fontSize: '14px', borderColor: errors.correo_responsable ? '#dc2626' : undefined }}
                  />
                  <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>El responsable recibirá un aviso cuando se envíe el alta</p>
                  <Err name="correo_responsable" />
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <Label style={{ fontSize: '12.5px', fontWeight: 600 }}>Teléfono corporativo</Label>
                    <button type="button" data-testid="cau-form-no-phone-toggle" onClick={() => handleNoPhone(!noPhone)}
                      style={{ display: 'flex', alignItems: 'center', gap: '4px', background: noPhone ? 'rgba(26,92,46,0.08)' : '#f5f5f5', color: noPhone ? '#1a5c2e' : '#888', border: `1px solid ${noPhone ? '#cfe3d6' : '#e0e0e0'}`, borderRadius: '20px', padding: '2px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', lineHeight: 1.8 }}>
                      <span>{noPhone ? '✓' : '○'}</span> Todavía por definir
                    </button>
                  </div>
                  <Input data-testid="cau-form-telefono-input" placeholder="+34 600 000 000" value={noPhone ? '' : form.telefono} onChange={e => handleChange('telefono', e.target.value)} disabled={noPhone}
                    style={{ height: '40px', fontSize: '14px', borderColor: errors.telefono ? '#dc2626' : undefined, background: noPhone ? '#f9f9f9' : undefined }} />
                  {noPhone && <p style={{ fontSize: '11px', color: '#3aaa62', marginTop: '3px' }}>✓ Línea pendiente de asignación</p>}
                  <Err name="telefono" />
                </div>
                <div>
                  <Label style={{ fontSize: '12.5px', fontWeight: 600 }}>Fecha de incorporación <span style={{ color: '#dc2626' }}>*</span></Label>
                  <Input data-testid="cau-form-fecha-incorporacion-input" type="date" value={form.fecha_incorporacion} onChange={e => handleChange('fecha_incorporacion', e.target.value)}
                    style={{ marginTop: '5px', height: '40px', fontSize: '14px', borderColor: errors.fecha_incorporacion ? '#dc2626' : undefined }} />
                  <Err name="fecha_incorporacion" />
                </div>
              </CardContent>
            </Card>

            {/* Right column: Work + Credentials stacked */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Información laboral */}
              <Card style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <CardHeader style={{ padding: '16px 20px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '28px', height: '28px', background: 'rgba(26,92,46,0.08)', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>🏢</div>
                    <div><CardTitle style={{ fontSize: '14px', color: '#1a5c2e' }}>Información laboral</CardTitle><CardDescription style={{ fontSize: '12px' }}>Lugar de trabajo</CardDescription></div>
                  </div>
                </CardHeader>
                <Separator />
                <CardContent style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <Label style={{ fontSize: '12.5px', fontWeight: 600 }}>Lugar de Trabajo <span style={{ color: '#dc2626' }}>*</span></Label>
                    <Popover open={openLugar} onOpenChange={setOpenLugar}>
                      <PopoverTrigger asChild>
                        <button
                          data-testid="cau-form-lugar-trabajo-select"
                          type="button"
                          style={{
                            marginTop: '5px',
                            height: '40px',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderRadius: '6px',
                            border: `1px solid ${errors.lugar_trabajo ? '#dc2626' : 'hsl(214 32% 91%)'}`,
                            background: '#fff',
                            padding: '0 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            textAlign: 'left'
                          }}
                        >
                          <span style={{ color: form.lugar_trabajo ? '#000' : '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {form.lugar_trabajo || "Buscar lugar de trabajo..."}
                          </span>
                          <ChevronsUpDown style={{ width: '16px', height: '16px', opacity: 0.5, flexShrink: 0 }} />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent style={{ width: '400px', padding: 0 }}>
                        <Command>
                          <CommandInput placeholder="Buscar lugar..." />
                          {lugaresTrabajo.length === 0 ? (
                            <CommandEmpty>Cargando lugares de trabajo...</CommandEmpty>
                          ) : (
                            <>
                              <CommandEmpty>No se encontró ningún lugar.</CommandEmpty>
                              <CommandGroup heading="Agregar nuevo" style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '8px', paddingBottom: '8px' }}>
                                <CommandItem
                                  onSelect={() => {
                                    setOpenLugar(false);
                                    setShowOtroDialog(true);
                                  }}
                                  style={{ cursor: 'pointer', background: '#fff3cd', borderRadius: '6px', fontWeight: 600, color: '#d97706' }}
                                >
                                  <span style={{ fontSize: '16px', marginRight: '8px' }}>➕</span>
                                  OTRO - Añadir nuevo lugar
                                </CommandItem>
                              </CommandGroup>
                              <CommandGroup style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {lugaresTrabajo.map((lugar) => (
                                  <CommandItem
                                    key={lugar}
                                    value={lugar}
                                    onSelect={() => {
                                      handleChange('lugar_trabajo', lugar);
                                      setOpenLugar(false);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <Check style={{ 
                                      width: '16px', 
                                      height: '16px', 
                                      marginRight: '8px',
                                      opacity: form.lugar_trabajo === lugar ? 1 : 0
                                    }} />
                                    <span style={{ fontSize: '13px' }}>{lugar}</span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </>
                          )}
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <Err name="lugar_trabajo" />
                  </div>
                </CardContent>
              </Card>

              {/* Credenciales */}
              <Card style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #cfe3d6', background: '#fafdf9' }}>
                <CardHeader style={{ padding: '16px 20px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '28px', height: '28px', background: 'rgba(26,92,46,0.08)', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>🔐</div>
                    <div><CardTitle style={{ fontSize: '14px', color: '#1a5c2e' }}>Credenciales de acceso IT</CardTitle><CardDescription style={{ fontSize: '12px' }}>Correo corporativo y contraseña temporal</CardDescription></div>
                  </div>
                </CardHeader>
                <Separator />
                <CardContent style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ background: '#fff8e1', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 12px' }}>
                    <p style={{ fontSize: '12px', color: '#92400e', margin: 0 }}>
                      <strong>⚠️</strong> La contraseña se envía en un <strong>correo separado</strong> al correo personal del empleado.
                    </p>
                  </div>
                  <div>
                    <Label style={{ fontSize: '12.5px', fontWeight: 600 }}>Correo corporativo <span style={{ color: '#dc2626' }}>*</span></Label>
                    <Input data-testid="cau-form-correo-corporativo-input" type="email" placeholder="nombre.apellido@valorizasm.com" value={form.correo_corporativo} onChange={e => handleChange('correo_corporativo', e.target.value)}
                      style={{ marginTop: '5px', height: '40px', fontSize: '14px', borderColor: errors.correo_corporativo ? '#dc2626' : undefined }} />
                    <Err name="correo_corporativo" />
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <Label style={{ fontSize: '12.5px', fontWeight: 600 }}>Contraseña de AD <span style={{ color: '#dc2626' }}>*</span></Label>
                      <button 
                        type="button" 
                        onClick={() => {
                          const newPassword = generateSecurePassword();
                          handleChange('contrasena', newPassword);
                          toast.success('Contraseña generada', { description: 'Contraseña segura ISO 27001 generada automáticamente' });
                        }}
                        data-testid="generate-password-button"
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '4px', 
                          background: 'linear-gradient(135deg, #1a5c2e, #2d8a4e)', 
                          color: '#fff', 
                          border: 'none', 
                          borderRadius: '6px', 
                          padding: '4px 10px', 
                          fontSize: '11px', 
                          fontWeight: 600, 
                          cursor: 'pointer', 
                          lineHeight: 1.8,
                          boxShadow: '0 2px 4px rgba(26,92,46,0.2)',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(26,92,46,0.3)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(26,92,46,0.2)';
                        }}
                      >
                        <span>🔐</span> Generar
                      </button>
                    </div>
                    <Input data-testid="cau-form-contrasena-input" type="text" placeholder="Ingresa la contraseña de AD" value={form.contrasena} onChange={e => handleChange('contrasena', e.target.value)}
                      style={{ marginTop: '5px', height: '40px', fontSize: '14px', fontFamily: 'IBM Plex Mono, monospace', borderColor: errors.contrasena ? '#dc2626' : undefined }} />
                    <p style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                      💡 La contraseña que asignaste en Active Directory. Se mostrará una sola vez al usuario via enlace seguro.
                    </p>
                    <Err name="contrasena" />
                  </div>
                </CardContent>
              </Card>

              {/* Material IT */}
              <Card style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <CardHeader style={{ padding: '16px 20px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '28px', height: '28px', background: 'rgba(26,92,46,0.08)', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>💻</div>
                    <div><CardTitle style={{ fontSize: '14px', color: '#1a5c2e' }}>Material IT</CardTitle><CardDescription style={{ fontSize: '12px' }}>Equipo asignado al usuario</CardDescription></div>
                  </div>
                </CardHeader>
                <Separator />
                <CardContent style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <Label style={{ fontSize: '12.5px', fontWeight: 600 }}>Portátil corporativo</Label>
                      <button 
                        type="button" 
                        onClick={() => handleChange('tienePortatil', !form.tienePortatil)}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '4px', 
                          background: !form.tienePortatil ? 'rgba(26,92,46,0.08)' : '#f5f5f5', 
                          color: !form.tienePortatil ? '#1a5c2e' : '#888', 
                          border: `1px solid ${!form.tienePortatil ? '#cfe3d6' : '#e0e0e0'}`, 
                          borderRadius: '20px', 
                          padding: '2px 10px', 
                          fontSize: '11px', 
                          fontWeight: 600, 
                          cursor: 'pointer', 
                          lineHeight: 1.8 
                        }}
                      >
                        <span>{!form.tienePortatil ? '✓' : '○'}</span> Sin portátil
                      </button>
                    </div>
                    {form.tienePortatil ? (
                      <p style={{ fontSize: '11px', color: '#3aaa62', marginTop: '3px' }}>✓ Se enviará guía de enrolado de portátil</p>
                    ) : (
                      <p style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>No se solicita portátil corporativo</p>
                    )}
                  </div>
                  
                  <div>
                    <Label style={{ fontSize: '12.5px', fontWeight: 600 }}>
                      Tipo de móvil corporativo 
                      <span style={{ fontSize: '11px', fontWeight: 400, color: '#888', marginLeft: '6px' }}>(opcional)</span>
                    </Label>
                    <Select value={form.tipoMovil} onValueChange={v => handleChange('tipoMovil', v)}>
                      <SelectTrigger style={{ marginTop: '5px', height: '40px', fontSize: '14px' }}>
                        <SelectValue placeholder="Seleccionar tipo (si aplica)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="COPE">COPE (Usuario Valoriza - Con enrolado)</SelectItem>
                        <SelectItem value="Quiosco">Quiosco (Sin usuario - Sin enrolado)</SelectItem>
                        <SelectItem value="Sin móvil">Sin móvil corporativo</SelectItem>
                      </SelectContent>
                    </Select>
                    {!form.tipoMovil && (
                      <p style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>
                        ℹ️ Puede dejarlo vacío si aún no tiene esta información
                      </p>
                    )}
                    {form.tipoMovil === 'COPE' && (
                      <p style={{ fontSize: '11px', color: '#3aaa62', marginTop: '3px' }}>✓ Se enviará guía de enrolado de móvil</p>
                    )}
                    {form.tipoMovil === 'Quiosco' && (
                      <p style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>ℹ️ Móvil configurado de fábrica (Econocom) - No requiere enrolado</p>
                    )}
                    {form.tipoMovil === 'Sin móvil' && (
                      <p style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>No se solicita móvil corporativo</p>
                    )}
                  </div>

                  {/* TOKEN EXPIRATION SELECTOR */}
                  <div>
                    <Label style={{ fontSize: '12.5px', fontWeight: 600 }}>
                      Expiración del enlace de activación 
                      <span style={{ color: '#dc2626' }}>*</span>
                    </Label>
                    <Select value={String(form.tokenExpiration)} onValueChange={v => handleChange('tokenExpiration', parseInt(v))}>
                      <SelectTrigger style={{ marginTop: '5px', height: '40px', fontSize: '14px' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="24">24 horas</SelectItem>
                        <SelectItem value="48">48 horas (2 días)</SelectItem>
                        <SelectItem value="72">72 horas (3 días)</SelectItem>
                        <SelectItem value="168">7 días (1 semana)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>
                      ⏱️ El usuario tendrá este tiempo para activar su cuenta después de recibir el email
                    </p>
                  </div>

                  {/* DRAFT PASSWORD DISPLAY */}
                  {draftPassword && (
                    <div style={{ 
                      background: '#e8f5e9', 
                      border: '2px solid #3aaa62', 
                      borderRadius: '10px', 
                      padding: '16px',
                      marginTop: '12px'
                    }}>
                      <p style={{ fontSize: '12px', fontWeight: 700, color: '#1a5c2e', margin: '0 0 10px 0' }}>
                        📋 Contraseña guardada en borrador:
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <code style={{ 
                          flex: 1,
                          background: '#fff', 
                          padding: '10px 14px', 
                          borderRadius: '6px',
                          fontFamily: 'monospace',
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#1a5c2e',
                          border: '1px solid #c5e1c7'
                        }}>
                          {draftPassword}
                        </code>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(draftPassword);
                            toast.success('Contraseña copiada');
                          }}
                          style={{
                            background: '#1a5c2e',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '10px 16px',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          📋 Copiar
                        </button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* SUBMIT BAR */}
          <motion.div variants={fadeUp}>
            <Card style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <CardContent style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: '#888' }}>
                    {draftId ? 'Borrador guardado. Puedes modificarlo y reenviarlo cuando quieras.' : 'Al enviar, se generan:'}
                  </span>
                  {!draftId && (
                    <>
                      <span style={{ background: '#f0faf3', color: '#1a5c2e', border: '1px solid #cfe3d6', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: 600 }}>📧 Bienvenida</span>
                      <span style={{ background: '#fff8e1', color: '#92400e', border: '1px solid #fde68a', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: 600 }}>🔐 Activación</span>
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button type="button" variant="outline" onClick={handleReset} disabled={loading} style={{ height: '42px', padding: '0 20px', fontSize: '14px' }}>
                    {draftId ? 'Cancelar' : 'Limpiar'}
                  </Button>
                  
                  {/* Save as Draft Button */}
                  <Button 
                    type="button" 
                    data-testid="cau-form-save-draft-button"
                    onClick={(e) => handleSubmit(e, true)} 
                    disabled={loading}
                    variant="outline"
                    style={{ 
                      height: '42px', 
                      padding: '0 22px', 
                      fontSize: '14px', 
                      fontWeight: 600,
                      borderColor: '#f59e0b',
                      color: '#92400e',
                      background: loading ? '#fef3c7' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '7px'
                    }}
                  >
                    {loading ? (
                      <>
                        <svg style={{ animation: 'spin 1s linear infinite', width: '15px', height: '15px' }} viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="rgba(146,64,14,0.3)" strokeWidth="3"/>
                          <path d="M12 2a10 10 0 0 1 10 10" stroke="#92400e" strokeWidth="3" strokeLinecap="round"/>
                        </svg>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <span>💾</span> {draftId ? 'Actualizar borrador' : 'Guardar borrador'}
                      </>
                    )}
                  </Button>

                  {/* Send/Process Button */}
                  <Button 
                    type="button"
                    data-testid="cau-form-submit-button"
                    onClick={(e) => handleSubmit(e, false)} 
                    disabled={loading}
                    style={{ 
                      background: loading ? '#9ca3af' : '#1a5c2e', 
                      color: '#fff', 
                      height: '42px', 
                      padding: '0 28px', 
                      fontSize: '14px', 
                      fontWeight: 600, 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '7px' 
                    }}
                  >
                    {loading ? (
                      <>
                        <svg style={{ animation: 'spin 1s linear infinite', width: '15px', height: '15px' }} viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3"/>
                          <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                        </svg>
                        Enviando…
                      </>
                    ) : (
                      <>
                        <span>📧</span> {draftId ? 'Enviar ahora' : 'Procesar Alta'}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

        </motion.div>
      </form>

      <div style={{ paddingBottom: '32px', textAlign: 'center' }}>
        <p style={{ fontSize: '11px', color: '#bbb', fontStyle: 'italic' }}>Powered by Valoriza's Innovation Team</p>
      </div>

      {/* Dialog para añadir nuevo lugar */}
      <OtroLugarDialog
        open={showOtroDialog}
        onOpenChange={setShowOtroDialog}
        value={otroLugar}
        onChange={setOtroLugar}
        onSave={handleSaveOtroLugar}
        saving={savingOtro}
      />

      <style>{`
        @keyframes spin { from{transform:rotate(0deg);}to{transform:rotate(360deg);} }
        @keyframes pulse-green { 0%,100%{opacity:1;}50%{opacity:0.4;} }
        @media (max-width: 900px) { .form-row-2 { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}

